/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import { CalendarBlankIcon, EnvelopeSimpleIcon, FloppyDiskIcon } from '@phosphor-icons/react';
import MaterialPhoneInput from '@orientaction/components/MaterialPhoneInput';
import { useAlert } from '@orientaction/hooks';
import { getFromLS } from '@orientaction/utils';
import { validate } from 'email-validator';
import { FC, FormEvent, MouseEvent, useMemo, useRef, useState } from 'react';
import { modifyUser } from '../../../../services/user.service';
import { apiServiceNative } from '../../../../utils/fetchNative';
import {
  DIPLOMA_OPTIONS,
  EMAIL_REGEX,
  GENDER_OPTIONS,
  LANGUAGE_OPTIONS,
} from '../../shared/constants';
import { hasRequiredBeneficiaryInfos, toDayOfBirth } from '../../shared/utils';
import { EMAIL_INVALID_MESSAGE, EMAIL_TAKEN_MESSAGE, PROFILE_UPDATED_MESSAGE } from '../constants';
import { useStyles } from '../styles';
import { ProfileFormValues, toLanguagePayload, userToProfileForm } from '../utils';

const qs = require('qs');

interface ProfileEditFormProps {
  user: any;
  onSaved: (updated: any) => void;
  onCancel: () => void;
}

const unwrapUsers = (payload: any): any[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

const ProfileEditForm: FC<ProfileEditFormProps> = ({ user, onSaved, onCancel }) => {
  const classes = useStyles();
  const alert = useAlert();
  const [form, setForm] = useState<ProfileFormValues>(() => userToProfileForm(user));
  const [emailError, setEmailError] = useState('');
  const [saving, setSaving] = useState(false);
  const birthInputRef = useRef<HTMLInputElement>(null);

  const openDatePicker = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const input = birthInputRef.current;
    if (!input) {
      return;
    }
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // Already open or not allowed in this browser
      }
    }
    input.focus();
  };

  const diplomaOptions = useMemo(() => {
    if (form.diploma && !DIPLOMA_OPTIONS.includes(form.diploma)) {
      return [form.diploma, ...DIPLOMA_OPTIONS];
    }
    return DIPLOMA_OPTIONS;
  }, [form.diploma]);

  const genderOptions = useMemo(() => {
    if (form.gender && !GENDER_OPTIONS.includes(form.gender)) {
      return [form.gender, ...GENDER_OPTIONS];
    }
    return GENDER_OPTIONS;
  }, [form.gender]);

  const update = (patch: Partial<ProfileFormValues>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    if (patch.email !== undefined) {
      setEmailError('');
    }
  };

  const emailIsTaken = async (email: string) => {
    const params = qs.stringify(
      {
        filters: { email: { $eq: email } },
        fields: ['id', 'email'],
      },
      { encodeValuesOnly: true }
    );
    const response = await apiServiceNative.get(`/api/users?${params}`);
    const found = unwrapUsers(response).find((item: any) => Number(item.id) !== Number(user.id));
    return Boolean(found);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!validate(email) || !EMAIL_REGEX.test(email)) {
      setEmailError(EMAIL_INVALID_MESSAGE);
      return;
    }

    setSaving(true);
    try {
      if (email !== (user.email || '').trim().toLowerCase()) {
        if (await emailIsTaken(email)) {
          setEmailError(EMAIL_TAKEN_MESSAGE);
          return;
        }
      }

      const language = toLanguagePayload(form.language);
      const payload = {
        email,
        username: email,
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        dayOfBirth: toDayOfBirth(form.dayOfBirth),
        gender: form.gender || null,
        postal_code: form.postal_code,
        town: form.town,
        phone: form.phone,
        language,
        diploma: form.diploma || null,
      };

      const token = getFromLS('userToken') || '';
      const updated = await modifyUser({
        ...payload,
        id: user.id,
        token,
      });
      alert.setOpenAlert(true, PROFILE_UPDATED_MESSAGE, 'success');
      onSaved({
        ...(updated?.data || {}),
        ...payload,
        language,
      });
    } catch (error: any) {
      const message = String(
        error?.response?.data?.error?.message || error?.message || error || ''
      );
      if (message.includes('email.taken') || message.includes('already taken')) {
        setEmailError(EMAIL_TAKEN_MESSAGE);
        return;
      }
      alert.openSimpleAlert();
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={classes.editForm} onSubmit={handleSubmit}>
      <div className={classes.editFormBody}>
      <h2 className={classes.sectionTitle}>Informations</h2>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="profile-email">
          Email
        </label>
        <TextField
          id="profile-email"
          className={classes.textField}
          variant="outlined"
          type="email"
          value={form.email}
          error={Boolean(emailError)}
          onChange={(event) => update({ email: event.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <EnvelopeSimpleIcon size={20} color="#8B90A5" />
              </InputAdornment>
            ),
          }}
        />
        {emailError ? <div className={classes.fieldError}>{emailError}</div> : null}
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="profile-firstname">
          Prénom
        </label>
        <TextField
          id="profile-firstname"
          className={classes.textField}
          variant="outlined"
          value={form.firstname}
          onChange={(event) => update({ firstname: event.target.value })}
        />
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="profile-lastname">
          Nom
        </label>
        <TextField
          id="profile-lastname"
          className={classes.textField}
          variant="outlined"
          value={form.lastname}
          onChange={(event) => update({ lastname: event.target.value })}
        />
      </div>

      <div className={classes.field}>
        <div className={classes.labelRow}>
          <label className={classes.label} htmlFor="profile-birth">
            Date de naissance
          </label>
          <span className={classes.optional}>Facultatif</span>
        </div>
        <TextField
          id="profile-birth"
          className={classes.textField}
          variant="outlined"
          type="date"
          value={form.dayOfBirth}
          inputRef={birthInputRef}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => update({ dayOfBirth: event.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <button
                  type="button"
                  className={classes.datePickerBtn}
                  aria-label="Ouvrir le calendrier"
                  onClick={openDatePicker}>
                  <CalendarBlankIcon size={20} color="#8B90A5" />
                </button>
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className={classes.field}>
        <div className={classes.labelRow}>
          <label className={classes.label} htmlFor="profile-gender">
            Sexe
          </label>
          <span className={classes.optional}>Facultatif</span>
        </div>
        <FormControl variant="outlined" className={classes.textField}>
          <Select
            id="profile-gender"
            value={form.gender}
            displayEmpty={true}
            onChange={(event) => update({ gender: event.target.value as string })}>
            <MenuItem value="">
              <span className={classes.placeholder}>Sélectionner</span>
            </MenuItem>
            {genderOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={classes.postalVilleRow}>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="profile-postal">
            Code Postal
          </label>
          <TextField
            id="profile-postal"
            className={classes.textField}
            variant="outlined"
            value={form.postal_code}
            onChange={(event) => update({ postal_code: event.target.value })}
          />
        </div>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="profile-town">
            Ville
          </label>
          <TextField
            id="profile-town"
            className={classes.textField}
            variant="outlined"
            value={form.town}
            onChange={(event) => update({ town: event.target.value })}
          />
        </div>
      </div>

      <div className={classes.field}>
        <label className={classes.label}>Téléphone</label>
        <div className={classes.phoneField}>
          <MaterialPhoneInput
            value={form.phone as any}
            name="phone"
            onChange={(value) => update({ phone: value || '' })}
          />
        </div>
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="profile-language">
          Langue
        </label>
        <FormControl variant="outlined" className={classes.textField}>
          <Select
            id="profile-language"
            name="language"
            value={form.language === '' ? '' : String(form.language)}
            displayEmpty={true}
            onChange={(event) => {
              const value = event.target.value as string;
              update({ language: value === '' ? '' : Number(value) });
            }}>
            <MenuItem value="">
              <span className={classes.placeholder}>Sélectionner</span>
            </MenuItem>
            {LANGUAGE_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={String(option.id)}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={classes.field}>
        <div className={classes.labelRow}>
          <label className={classes.label} htmlFor="profile-diploma">
            Diplôme
          </label>
          <span className={classes.optional}>Facultatif</span>
        </div>
        <FormControl variant="outlined" className={classes.textField}>
          <Select
            id="profile-diploma"
            value={form.diploma}
            displayEmpty={true}
            onChange={(event) => update({ diploma: event.target.value as string })}>
            <MenuItem value="">
              <span className={classes.placeholder}>Sélectionner</span>
            </MenuItem>
            {diplomaOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      </div>

      <div className={classes.formFooter}>
        <Button
          type="button"
          variant="outlined"
          className={classes.secondaryBtn}
          disabled={saving}
          onClick={onCancel}>
          Annuler
        </Button>
        <Button
          type="submit"
          className={classes.saveBtn}
          startIcon={<FloppyDiskIcon size={18} />}
          disabled={
            saving ||
            !form.email.trim() ||
            !hasRequiredBeneficiaryInfos(form)
          }>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </form>
  );
};

export default ProfileEditForm;
